import type { ReactNode } from 'react';
import { NavLink, Outlet, useMatch } from 'react-router';
import { RefreshIcon, TerminalIcon, UserIcon } from '@/components/icons';
import { Logo } from '@/components/Logo';
import { AccountMenu, SessionStats } from '@/features/auth';
import { cn } from '@/lib/cn';

const NAV: { to: string; label: string; icon: ReactNode }[] = [
  { to: '/learn', label: 'Learn', icon: <TerminalIcon size={22} /> },
  { to: '/practice', label: 'Practice', icon: <RefreshIcon size={22} /> },
  { to: '/profile', label: 'Profile', icon: <UserIcon size={22} /> },
];

/**
 * App screens: 64px top bar (logo, nav pills, streak, XP, account) and a 72px bottom tab bar on mobile.
 * The challenge workspace renders its own compact 60px bar.
 */
export function AppLayout() {
  const isChallenge = useMatch('/learn/:track/:chapter/:challenge');
  if (isChallenge) return <Outlet />;

  return (
    <div className="flex min-h-dvh flex-col">
      <a
        href="#content"
        className="sr-only focus:not-sr-only focus:absolute focus:left-4 focus:top-4 focus:z-50 focus:rounded-md focus:bg-surface focus:px-4 focus:py-2"
      >
        Skip to content
      </a>
      <header className="sticky top-0 z-30 flex h-14 shrink-0 items-center gap-10 border-b border-line bg-surface px-4 md:h-16 md:px-8 xl:px-20">
        <Logo to="/learn" size="md" className="max-md:[&>span:first-child]:size-[26px]" />
        <nav aria-label="Main" className="hidden gap-2 md:flex">
          {NAV.map((item) => (
            <NavLink
              key={item.to}
              to={item.to}
              className={({ isActive }) =>
                cn(
                  'flex min-h-10 items-center rounded-md px-3.5 text-body-app no-underline',
                  isActive
                    ? 'bg-surface-muted font-medium text-ink hover:text-ink'
                    : 'text-ink-soft hover:bg-surface-muted hover:text-ink',
                )
              }
            >
              {item.label}
            </NavLink>
          ))}
        </nav>
        <div className="ml-auto flex items-center gap-4">
          <SessionStats variant="short" />
          <div className="hidden md:block">
            <AccountMenu />
          </div>
        </div>
      </header>
      <div id="content" className="flex flex-1 flex-col pb-[72px] md:pb-0">
        <Outlet />
      </div>
      <nav
        aria-label="Main"
        className="fixed inset-x-0 bottom-0 z-30 grid h-[72px] grid-cols-3 border-t border-line bg-surface px-2 pb-2 md:hidden"
      >
        {NAV.map((item) => (
          <NavLink
            key={item.to}
            to={item.to}
            className={({ isActive }) =>
              cn(
                'flex flex-col items-center justify-center gap-1 text-xs no-underline',
                isActive ? 'font-semibold text-ink hover:text-ink' : 'text-ink-muted hover:text-ink',
              )
            }
          >
            {item.icon}
            {item.label}
          </NavLink>
        ))}
      </nav>
    </div>
  );
}
